import { useIsAuthenticated } from '@azure/msal-react';
import { useAsyncData } from 'shared';
import { useApi, useDebounce } from 'App';
import { useAuthorization, useUser } from 'Auth';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { SentryApiClient } from '_generated/api';
import { UserType } from 'Users/userTypes';

export interface SentryUserData {
  user?: SentryApiClient.UserDTO;
  isLoading: boolean;
}

async function hangfireAuth(token: string) {
  await fetch('/api/hangfire-dashboard/session', {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });
}

const SentryUserContext = createContext<SentryUserData | null>(null);

export interface SentryUserProviderProps {}

export const SentryUserProvider: React.FC<SentryUserProviderProps> = ({
  children,
}) => {
  const isAuthenticated = useIsAuthenticated();
  const [token] = useAuthorization();

  const canFetchUser = useMemo(() => isAuthenticated && token != null, [
    isAuthenticated,
    token,
  ]);

  const msalUser = useUser();
  const { userApi: api } = useApi();

  const { data: user, isLoading } = useAsyncData(
    async () =>
      msalUser && canFetchUser
        ? api.getById(msalUser.localAccountId)
        : undefined,
    [msalUser, canFetchUser, api]
  );

  useEffect(() => {
    if (user != null && user.userTypeId === UserType.Administrator) {
      hangfireAuth(token);
    }
  }, [user, token]);

  return (
    <SentryUserContext.Provider value={{ user, isLoading }}>
      {children}
    </SentryUserContext.Provider>
  );
};

export function useSentryUser() {
  const result = useContext(SentryUserContext);

  if (!result) {
    throw new Error(
      'useSentryUser() cannot be used without being wrapped by a SentryUserProvider'
    );
  }

  return result;
}
